/* The product information group enables one open information row per Raith buying panel.
 * Keep the browser's details activation (including Enter/Space), without mixing
 * synchronous sibling closure with Prestige's delayed animation callbacks.
 */
(() => {
  if (customElements.get('raith-product-information')) return;

  class RaithProductInformation extends HTMLElement {
    connectedCallback() {
      if (this.group) return;
      this.group = this;
      this.onClick = this.handleClick.bind(this);
      this.onToggle = this.handleToggle.bind(this);
      this.onEditor = this.handleEditor.bind(this);
      this.group.addEventListener('click', this.onClick, true);
      this.group.addEventListener('toggle', this.onToggle, true);
      this.group.addEventListener('shopify:block:select', this.onEditor, true);
      this.group.addEventListener('shopify:block:deselect', this.onEditor, true);
      const rows = this.rows();
      const initiallyOpen = rows.find(row => row.open);
      rows.forEach(row => this.setState(row, row === initiallyOpen));
    }

    disconnectedCallback() {
      if (!this.group) return;
      this.group.removeEventListener('click', this.onClick, true);
      this.group.removeEventListener('toggle', this.onToggle, true);
      this.group.removeEventListener('shopify:block:select', this.onEditor, true);
      this.group.removeEventListener('shopify:block:deselect', this.onEditor, true);
      this.group = null;
    }

    rows() {
      return Array.from(this.children).map(block => {
        if (!block.classList.contains('vl-buybox__block')) return null;
        switch (block.dataset.blockType) {
          case 'description':
          case 'accordion':
            return block.querySelector(':scope > accordion-disclosure > details');
          case 'trustpilot':
            return block.querySelector(':scope > raith-trustpilot > details');
          case 'delivery':
            return block.querySelector(':scope > div.accordion > details[data-raith-delivery-disclosure]');
          default: return null;
        }
      }).filter(row => row?.firstElementChild?.tagName === 'SUMMARY');
    }

    setState(row, open) {
      row.open = open;
      row.setAttribute('aria-expanded', String(open));
    }

    closeOthers(current) {
      this.rows().forEach(row => {
        if (row !== current) this.setState(row, false);
      });
    }

    handleClick(event) {
      const summary = event.target.closest?.('summary');
      const row = summary?.parentElement;
      if (event.defaultPrevented || !this.rows().includes(row) || summary !== row.firstElementChild) return;
      // Do not cancel the default action. Suppress only the vendor click handler.
      event.stopPropagation();
      if (!row.open) this.closeOthers(row);
      row.setAttribute('aria-expanded', String(!row.open));
    }

    handleToggle(event) {
      if (!this.rows().includes(event.target)) return;
      const row = event.target;
      row.setAttribute('aria-expanded', String(row.open));
      if (row.open) this.closeOthers(row);
    }

    handleEditor(event) {
      const block = event.target.closest?.('.vl-buybox__block');
      if (block?.parentElement !== this.group) return;
      const row = this.rows().find(candidate => block.contains(candidate));
      if (!row) return;
      // Editor attributes can target the block wrapper or its native disclosure.
      event.stopPropagation();
      const open = event.type === 'shopify:block:select';
      if (open) this.closeOthers(row);
      this.setState(row, open);
    }
  }

  customElements.define('raith-product-information', RaithProductInformation);
})();

(() => {
  if (customElements.get('raith-trustpilot-feed')) return;
  const bootstrapUrl = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
  let bootstrap;
  const supportedTheme = () => window.Shopify?.shop === 'raithclothing.myshopify.com' && /^[1-9]\d*$/.test(String(window.Shopify?.theme?.id)) && ['main', 'unpublished'].includes(window.Shopify?.theme?.role);
  function provider() {
    if (typeof window.Trustpilot?.loadFromElement === 'function') return Promise.resolve(window.Trustpilot);
    if (bootstrap) return bootstrap;
    bootstrap = new Promise((resolve, reject) => {
      let script = [...document.scripts].find(node => node.src === bootstrapUrl);
      let timer, attempts = 0, done = false;
      const finish = (error) => {
        if (done) return;
        done = true; clearTimeout(timer);
        script?.removeEventListener('error', failed);
        if (error) reject(error); else resolve(window.Trustpilot);
      };
      const failed = () => finish(new Error('Trustpilot unavailable'));
      const check = () => {
        if (typeof window.Trustpilot?.loadFromElement === 'function') return finish();
        if (++attempts >= 160) return failed();
        timer = setTimeout(check, 50);
      };
      if (!script) {
        script = document.createElement('script'); script.src = bootstrapUrl; script.async = true;
        script.addEventListener('error', failed, {once: true}); document.head.append(script);
      } else script.addEventListener('error', failed, {once: true});
      check();
    });
    return bootstrap;
  }
  class RaithTrustpilotFeed extends HTMLElement {
    connectedCallback() {
      if (this.row || !supportedTheme()) return;
      this.row = this.closest('details'); if (!this.row) return;
      this.generation = (this.generation || 0) + 1;
      this.openHandler = () => { if (this.row?.open) this.mount(); };
      this.row.addEventListener('toggle', this.openHandler);
      this.openHandler();
    }
    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.row?.removeEventListener('toggle', this.openHandler); this.row = null; this.pending = false;
    }
    async mount() {
      if (this.mounted || this.pending || !supportedTheme() || !this.isConnected || !this.row?.open) return;
      const generation = this.generation; this.pending = true;
      try {
        const api = await provider();
        if (this.generation !== generation || !this.isConnected || !this.row?.open || !supportedTheme()) return;
        const widget = document.createElement('div'); widget.className = 'trustpilot-widget';
        const language = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
        const locale = ({de:'de-DE', nl:'nl-NL'})[language] || 'en-GB';
        for (const [name, value] of Object.entries({locale, 'template-id':'53aa8807dec7e10d38f59f32', 'businessunit-id':'6021633d863387000182c947', 'style-height':'100px', 'style-width':'100%', theme:'light', 'text-color':'#292927'})) widget.setAttribute('data-'+name,value);
        const link = document.createElement('a'); link.href = 'https://uk.trustpilot.com/review/raith-clo.com'; link.textContent = 'Trustpilot'; widget.append(link);
        this.append(widget);
        try { api.loadFromElement(widget); this.mounted = true; } catch (error) { widget.remove(); throw error; }
      } catch { /* The independent profile link remains usable. */ }
      finally { if (this.generation === generation) this.pending = false; }
    }
  }
  customElements.define('raith-trustpilot-feed', RaithTrustpilotFeed);
})();

